import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Helmet } from "react-helmet-async";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useMutation } from "react-query";
import client from "../../API";
import { replaceConsoleError, replaceConsoleLog } from "../../Utils/toastNotifications";
import { validateEmail, validatePassword, capitalizeFirstLetter } from "../../Utils";

/* ─── Fonts ──────────────────────────────────────────────── */
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href =
  "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";
if (!document.head.querySelector('link[href*="DM+Serif"]')) {
  document.head.appendChild(fontLink);
}

/* ─── Theme ──────────────────────────────────────────────── */
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#1a1a2e" },
    secondary: { main: "#c9a84c" },
  },
  typography: { fontFamily: "'DM Sans', sans-serif" },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            background: "#fafafa",
            fontSize: 14,
            "& fieldset": { borderColor: "#e2e2e2" },
            "&:hover fieldset": { borderColor: "#c9a84c" },
            "&.Mui-focused fieldset": { borderColor: "#1a1a2e", borderWidth: 1.5 },
          },
          "& .MuiInputLabel-root.Mui-focused": { color: "#1a1a2e" },
        },
      },
    },
  },
});

/* ─── Mobile Brand Banner ────────────────────────────────── */
function MobileBrandBanner({ company, companyData, loadingCompany }) {
  const displayName = companyData?.name || company || "Blitz";
  const logo = companyData?.phphref || null;
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div style={{
      background: "linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 100%)",
      padding: "28px 24px 24px",
      display: "flex",
      alignItems: "center",
      gap: 16,
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute",
        width: 200, height: 200,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(201,168,76,0.15) 0%, transparent 70%)",
        top: "50%", right: -40,
        transform: "translateY(-50%)",
        pointerEvents: "none",
      }} />

      <div style={{
        width: 52, height: 52,
        borderRadius: 14,
        background: "linear-gradient(135deg, rgba(201,168,76,0.15), rgba(201,168,76,0.3))",
        border: "1.5px solid rgba(201,168,76,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, overflow: "hidden",
        boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
      }}>
        {loadingCompany ? (
          <div style={{ width: "100%", height: "100%", background: "rgba(201,168,76,0.1)" }} />
        ) : logo ? (
          <img src={logo} alt={displayName}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <span style={{ fontSize: 22, fontFamily: "'DM Serif Display', serif", color: "#c9a84c" }}>
            {initial}
          </span>
        )}
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>
        <p style={{
          fontSize: 10, fontWeight: 600,
          letterSpacing: "0.18em", textTransform: "uppercase",
          color: "#c9a84c", margin: "0 0 4px", opacity: 0.9,
        }}>
          You're joining
        </p>
        <h2 style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: 22, color: "#ffffff",
          margin: 0, fontWeight: 400, lineHeight: 1.1,
        }}>
          {loadingCompany ? <span style={{ opacity: 0.3 }}>Loading…</span> : displayName}
        </h2>
      </div>

      <div style={{
        position: "absolute", bottom: 10, right: 16,
        display: "flex", alignItems: "center", gap: 5, opacity: 0.35,
      }}>
        <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#c9a84c" }} />
        <span style={{ fontSize: 9, color: "#fff", letterSpacing: "0.15em", fontWeight: 600 }}>
          POWERED BY BLITZ
        </span>
        <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#c9a84c" }} />
      </div>
    </div>
  );
}

/* ─── Desktop Brand Panel ────────────────────────────────── */
function DesktopBrandPanel({ company, companyData, loadingCompany }) {
  const displayName = companyData?.name || company || "Your Team";
  const logo = companyData?.phphref || null;
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div style={{
      width: 400, flexShrink: 0,
      height: "100vh",
      background: "linear-gradient(155deg, #0f0f1e 0%, #1a1a2e 55%, #16213e 100%)",
      display: "flex", flexDirection: "column",
      justifyContent: "center", alignItems: "center",
      padding: "60px 48px",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", inset: 0, opacity: 0.035,
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        backgroundSize: "150px 150px", pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute",
        width: 420, height: 420, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(201,168,76,0.16) 0%, transparent 70%)",
        top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", pointerEvents: "none",
      }} />

      <div style={{ position: "relative", zIndex: 1, textAlign: "center", width: "100%" }}>
        {/* Logo */}
        <div style={{
          width: 96, height: 96,
          borderRadius: 24,
          background: "linear-gradient(135deg, rgba(201,168,76,0.13), rgba(201,168,76,0.28))",
          border: "1.5px solid rgba(201,168,76,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 28px", overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)",
        }}>
          {loadingCompany ? (
            <div style={{ width: "100%", height: "100%", background: "rgba(201,168,76,0.08)" }} />
          ) : logo ? (
            <img src={logo} alt={displayName}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <span style={{
              fontSize: 40, fontFamily: "'DM Serif Display', serif",
              color: "#c9a84c", fontWeight: 400, lineHeight: 1,
            }}>
              {initial}
            </span>
          )}
        </div>

        <p style={{
          fontSize: 11, fontWeight: 600, letterSpacing: "0.2em",
          textTransform: "uppercase", color: "#c9a84c",
          marginBottom: 12, opacity: 0.9,
        }}>
          You're joining
        </p>
        <h1 style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: 34, color: "#ffffff",
          lineHeight: 1.15, marginBottom: 16, fontWeight: 400,
        }}>
          {loadingCompany ? <span style={{ opacity: 0.3 }}>Loading...</span> : displayName}
        </h1>
        <p style={{
          fontSize: 14, color: "rgba(255,255,255,0.42)",
          lineHeight: 1.75, maxWidth: 260, margin: "0 auto 40px",
        }}>
          Create your account to access payments, contracts, and collaboration tools.
        </p>

        <div style={{
          width: 40, height: 1,
          background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)",
          margin: "0 auto 40px",
        }} />

        <div style={{ display: "flex", gap: 24, justifyContent: "center", flexWrap: "wrap" }}>
          {[
            { icon: "🔒", label: "Encrypted" },
            { icon: "⚡", label: "Instant Payouts" },
            { icon: "✓", label: "Verified" },
          ].map((t) => (
            <div key={t.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 18, opacity: 0.65 }}>{t.icon}</span>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: "0.06em" }}>
                {t.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        position: "absolute", bottom: 28, left: "50%",
        transform: "translateX(-50%)",
        display: "flex", alignItems: "center", gap: 8,
        opacity: 0.35, whiteSpace: "nowrap",
      }}>
        <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#c9a84c" }} />
        <span style={{ fontSize: 10, color: "#fff", letterSpacing: "0.16em", fontWeight: 600 }}>
          POWERED BY BLITZ
        </span>
        <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#c9a84c" }} />
      </div>
    </div>
  );
}

/* ─── Register ───────────────────────────────────────────── */
const Register = () => {
  const navigate = useNavigate();
  const { company } = useParams();
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery("(max-width:768px)");

  const [companyData, setCompanyData] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(!!company);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [userInfo, setUserInfo] = useState({
    firstName: "", lastName: "", email: "",
    password: "", confirmPassword: "",
    companyName: company || "",
    phoneNumber: "", streetAddress: "",
    city: "", state: "", country: "",
  });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const showSnack = (message, severity = "error") =>
    setSnack({ open: true, message, severity });

  /* ── Dynamic meta ── */
  const displayName = companyData?.name || company || null;
  const pageTitle = displayName
    ? `Join ${displayName} on Blitz`
    : "Create an Account — Blitz";
  const ogDescription = displayName
    ? `${displayName} invited you to Blitz — instant payments, contracts, and collaboration.`
    : "Create your Blitz account to access payments, contracts, and collaboration tools.";
  const ogImage = companyData?.phphref || "https://www.useblitz.co/og-default.png";
  const canonicalUrl = typeof window !== "undefined" ? window.location.href : "";

  /* ── Fetch company ── */
  const { mutate: fetchCompanies } = useMutation(client.companies.list, {
    onSuccess: (data) => {
      const list = data?.companies || data || [];
      const match =
        list.find((c) => c.name?.toLowerCase() === (company || "").toLowerCase()) ||
        list[0];
      setCompanyData(match || null);
      setLoadingCompany(false);
    },
    onError: () => setLoadingCompany(false),
  });

  useEffect(() => {
    if (company) fetchCompanies({ search: company, limit: 5 });
  }, [company]);

  /* ── Register ── */
  const { mutate: register, isLoading } = useMutation(client.users.register, {
    onSuccess: (data) => {
      const claimToken =
        searchParams.get("claim_token") ||
        searchParams.get("claimToken") ||
        sessionStorage.getItem("claim_funds_token");
      showSnack(
        data?.redirect_to || claimToken
          ? "Account created! Redirecting to claim your funds..."
          : "Account created! Taking you to login...",
        "success"
      );
      setTimeout(() => {
        if (data?.redirect_to) navigate(data.redirect_to);
        else if (claimToken)
          navigate(`/claim-funds?token=${encodeURIComponent(claimToken)}`);
        else navigate("/login");
      }, 1500);
    },
    onError: (error) => {
      replaceConsoleError("Registration error", error);
      const d = error.response?.data;
      showSnack(
        d?.missing ? `Missing: ${d.missing.join(", ")}`
          : d?.message || d?.error || "An error occurred. Please try again."
      );
    },
  });

  /* ── Submit ── */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!termsAccepted) return showSnack("You must accept the Terms and Conditions.");
    const required = ["firstName","lastName","email","password","confirmPassword","companyName"];
    for (const f of required) {
      if (!userInfo[f]?.trim())
        return showSnack(`${capitalizeFirstLetter(f === "companyName" ? "Company Code" : f)} is required.`);
    }
    if (!validateEmail(userInfo.email)) return showSnack("Invalid email address.");
    if (!validatePassword(userInfo.password)) return showSnack("Password must be at least 8 characters.");
    if (userInfo.password !== userInfo.confirmPassword) return showSnack("Passwords do not match.");

    const params = {
      first_name: userInfo.firstName.trim(),
      last_name:  userInfo.lastName.trim(),
      email:      userInfo.email.trim().toLowerCase(),
      password:   userInfo.password,
      company_name: userInfo.companyName.trim(),
    };
    if (userInfo.phoneNumber.trim())   params.phone_number = userInfo.phoneNumber.trim();
    if (userInfo.streetAddress.trim()) params.address      = userInfo.streetAddress.trim();
    if (userInfo.city.trim())          params.city         = userInfo.city.trim();
    if (userInfo.state.trim())         params.state        = userInfo.state.trim();
    if (userInfo.country.trim())       params.country      = userInfo.country.trim();

    const claimToken =
      searchParams.get("claim_token") ||
      searchParams.get("claimToken") ||
      sessionStorage.getItem("claim_funds_token");
    if (claimToken) params.claim_token = claimToken;

    replaceConsoleLog("Sending registration data", params);
    register(params);
  };

  /* ── Field helper ── */
  const field = (label, key, opts = {}) => (
    <TextField
      key={key}
      label={label}
      variant="outlined"
      fullWidth
      disabled={isLoading}
      type={opts.type || "text"}
      required={opts.required !== false}
      value={userInfo[key]}
      onChange={(e) => setUserInfo({ ...userInfo, [key]: e.target.value })}
      error={opts.error}
      helperText={opts.helperText}
      placeholder={opts.placeholder}
      sx={{ mb: opts.mb !== undefined ? opts.mb : 2 }}
    />
  );

  const submitDisabled = isLoading || !termsAccepted;

  /* ── Shared form JSX ── */
  const formContent = (
    <form onSubmit={handleSubmit} noValidate>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
        {field("First Name", "firstName", { mb: 0 })}
        {field("Last Name",  "lastName",  { mb: 0 })}
      </div>

      {field("Email Address", "email", {
        type: "email",
        error: !validateEmail(userInfo.email) && userInfo.email.length > 0,
        helperText: !validateEmail(userInfo.email) && userInfo.email.length > 0
          ? "Enter a valid email address" : "",
      })}
      {field("Password", "password", {
        type: "password",
        error: !validatePassword(userInfo.password) && userInfo.password.length > 0,
        helperText: !validatePassword(userInfo.password) && userInfo.password.length > 0
          ? "At least 8 characters" : "",
      })}
      {field("Confirm Password", "confirmPassword", {
        type: "password",
        error: userInfo.password !== userInfo.confirmPassword && userInfo.confirmPassword.length > 0,
        helperText: userInfo.password !== userInfo.confirmPassword && userInfo.confirmPassword.length > 0
          ? "Passwords don't match" : "",
      })}
      {field("Company Code", "companyName", {
        helperText: "The company code provided by your administrator",
        mb: 0,
      })}

      <p style={{
        fontSize: 11, fontWeight: 600, letterSpacing: "0.14em",
        textTransform: "uppercase", color: "#b0a898",
        margin: "22px 0 13px",
      }}>
        Optional Details
      </p>
      <div style={{
        border: "1px solid #e8e5df", borderRadius: 12,
        padding: "18px 18px 4px", background: "#faf9f7", marginBottom: 22,
      }}>
        {field("Phone Number",   "phoneNumber",   { required: false, placeholder: "+1 (555) 123-4567" })}
        {field("Street Address", "streetAddress", { required: false, placeholder: "123 Main St" })}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
          {field("City",  "city",  { required: false, mb: 0 })}
          {field("State", "state", { required: false, mb: 0 })}
        </div>
        {field("Country", "country", { required: false, placeholder: "United States", mb: 0 })}
        <Box sx={{ mb: 2 }} />
      </div>

      <FormControlLabel
        sx={{ mb: 3, alignItems: "flex-start" }}
        control={
          <Checkbox
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            sx={{
              color: "#d0cdc8",
              "&.Mui-checked": { color: "#1a1a2e" },
              padding: "4px 8px 4px 9px", mt: "1px",
            }}
          />
        }
        label={
          <span style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>
            I agree to the{" "}
            <RouterLink to="/terms" target="_blank" rel="noopener noreferrer"
              style={{
                color: "#1a1a2e", fontWeight: 600,
                textDecoration: "underline",
                textDecorationColor: "#c9a84c",
                textDecorationThickness: 1.5,
              }}>
              Terms & Conditions
            </RouterLink>
          </span>
        }
      />

      <button
        type="submit"
        disabled={submitDisabled}
        className="reg-submit"
        style={{
          width: "100%", padding: "15px 0",
          borderRadius: 10,
          background: submitDisabled
            ? "#d4d2cc"
            : "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
          color: "#fff",
          fontSize: 15, fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif",
          border: "none",
          cursor: submitDisabled ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 10, letterSpacing: "0.02em",
          boxShadow: submitDisabled ? "none" : "0 4px 20px rgba(26,26,46,0.25)",
          opacity: submitDisabled ? 0.65 : 1,
        }}
      >
        {isLoading
          ? <><CircularProgress size={16} sx={{ color: "#fff" }} /> Creating account...</>
          : "Create Account →"}
      </button>

      <div style={{ textAlign: "center", marginTop: 22 }}>
        <span style={{ fontSize: 13, color: "#999" }}>
          Already have an account?{" "}
          <RouterLink to="/login"
            style={{ color: "#1a1a2e", fontWeight: 600, textDecoration: "underline" }}>
            Sign in
          </RouterLink>
        </span>
      </div>
    </form>
  );

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={ogDescription} />
        {/* Open Graph — drives iMessage, Slack, WhatsApp, LinkedIn previews */}
        <meta property="og:title"       content={pageTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image"       content={ogImage} />
        <meta property="og:url"         content={canonicalUrl} />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="Blitz" />
        {/* Twitter card */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={pageTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image"       content={ogImage} />
      </Helmet>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: none; }
        }
        .reg-fade   { animation: fadeUp 0.45s ease both; }
        .reg-submit { transition: transform 0.18s ease, box-shadow 0.18s ease !important; }
        .reg-submit:not([disabled]):hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(26,26,46,0.38) !important;
        }
      `}</style>

      {isMobile ? (
        /* ══════════ MOBILE — stacked ══════════ */
        <div style={{
          minHeight: "100vh",
          background: "#f4f3ee",
          display: "flex",
          flexDirection: "column",
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <MobileBrandBanner
            company={company}
            companyData={companyData}
            loadingCompany={loadingCompany}
          />
          <div className="reg-fade" style={{ flex: 1, padding: "28px 20px 52px" }}>
            <div style={{ marginBottom: 28 }}>
              <h2 style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: 26, color: "#0f0f1e",
                fontWeight: 400, margin: "0 0 6px", lineHeight: 1.2,
              }}>
                Create your account
              </h2>
              <p style={{ fontSize: 14, color: "#888", margin: 0, lineHeight: 1.6 }}>
                {companyData?.name
                  ? `Join ${companyData.name} on Blitz.`
                  : "Fill in your details to get started."}
              </p>
            </div>
            {formContent}
          </div>
        </div>
      ) : (
        /* ══════════ DESKTOP — side-by-side ══════════ */
        <div style={{
          height: "100vh",
          display: "flex",
          fontFamily: "'DM Sans', sans-serif",
          overflow: "hidden",   /* ← locks viewport; each panel scrolls independently */
          background: "#f4f3ee",
        }}>
          {/* Left: fixed-height brand panel */}
          <DesktopBrandPanel
            company={company}
            companyData={companyData}
            loadingCompany={loadingCompany}
          />

          {/* Right: independently scrollable form */}
          <div style={{
            flex: 1,
            height: "100vh",
            overflowY: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: "60px 48px",
          }}>
            <div className="reg-fade" style={{ width: "100%", maxWidth: 460 }}>
              <div style={{ marginBottom: 36 }}>
                <h2 style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 30, color: "#0f0f1e",
                  fontWeight: 400, margin: "0 0 8px", lineHeight: 1.2,
                }}>
                  Create your account
                </h2>
                <p style={{ fontSize: 14, color: "#888", margin: 0, lineHeight: 1.6 }}>
                  {companyData?.name
                    ? `Join ${companyData.name} on Blitz to get started.`
                    : "Fill in your details below to get started."}
                </p>
              </div>
              {formContent}
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default Register;